/**
 * Maps emoji strings to Lucide icon names for branded rendering.
 * Used by both web (lucide-react) and mobile (lucide-react-native).
 *
 * All icons use the brand color (deep jade #1d8e85) for consistent branding.
 * Returns { icon: string, color: string } where icon is the Lucide component name.
 * If no mapping exists, returns null (caller should fall back to emoji).
 */

const BRAND = '#1d8e85';

const EMOJI_TO_ICON = {
  // Landmarks & attractions
  '🗼': { icon: 'Landmark', color: BRAND },           // Tower
  '🗽': { icon: 'Landmark', color: BRAND },            // Statue of Liberty
  '🏛️': { icon: 'Landmark', color: BRAND },            // Monument / Museum
  '🏺': { icon: 'Landmark', color: BRAND },            // Ancient site
  '🧱': { icon: 'ToyBrick', color: BRAND },            // Great Wall
  '🕌': { icon: 'Building', color: BRAND },            // Mosque
  '🏰': { icon: 'Castle', color: BRAND },              // Castle / Palace

  // Culture & museums
  '🎨': { icon: 'Palette', color: BRAND },             // Museum / Art
  '⛪': { icon: 'Church', color: BRAND },               // Church / Cathedral
  '⛩️': { icon: 'Landmark', color: BRAND },             // Temple / Shrine
  '🎭': { icon: 'Drama', color: BRAND },               // Theater / Show
  '📸': { icon: 'Camera', color: BRAND },              // Photography

  // Tea & ceremony
  '🍵': { icon: 'CupSoda', color: BRAND },             // Tea ceremony

  // Exploration & walking
  '🏙️': { icon: 'Building2', color: BRAND },           // City stroll
  '📍': { icon: 'MapPin', color: BRAND },              // Location / Pin
  '🎯': { icon: 'Target', color: BRAND },              // Destination / Goal

  // Nature & outdoors
  '🏖️': { icon: 'Umbrella', color: BRAND },            // Beach
  '⛰️': { icon: 'Mountain', color: BRAND },            // Mountain / Hiking
  '🌊': { icon: 'Waves', color: BRAND },               // Lake / River
  '🌳': { icon: 'TreePine', color: BRAND },            // Park / Garden
  '💦': { icon: 'Droplets', color: BRAND },            // Waterfall
  '🌅': { icon: 'Sunrise', color: BRAND },             // Sunset / Sunrise
  '🌟': { icon: 'Star', color: BRAND },                // Stargazing
  '🌿': { icon: 'Leaf', color: BRAND },                // Garden / Nature
  '🦁': { icon: 'PawPrint', color: BRAND },            // Safari / Wildlife
  '🐠': { icon: 'Fish', color: BRAND },                // Aquarium

  // Adventure & sports
  '🏄': { icon: 'Waves', color: BRAND },               // Surf
  '⛷️': { icon: 'Snowflake', color: BRAND },           // Ski
  '🤿': { icon: 'Glasses', color: BRAND },             // Diving
  '🛶': { icon: 'Ship', color: BRAND },                // Kayak
  '🚴': { icon: 'Bike', color: BRAND },                // Cycling
  '🧗': { icon: 'MountainSnow', color: BRAND },        // Climbing
  '🎢': { icon: 'Zap', color: BRAND },                 // Zipline
  '🪂': { icon: 'Wind', color: BRAND },                // Paragliding
  '⛳': { icon: 'Flag', color: BRAND },                 // Golf

  // Entertainment
  '🎰': { icon: 'Dice5', color: BRAND },               // Casino
  '🪩': { icon: 'Music', color: BRAND },               // Nightlife
  '💆': { icon: 'Flower2', color: BRAND },             // Spa
  '🧘': { icon: 'Flower2', color: BRAND },             // Yoga / Meditation
  '🏊': { icon: 'Waves', color: BRAND },               // Pool / Swimming

  // Shopping & markets
  '🛍️': { icon: 'ShoppingBag', color: BRAND },         // Market
  '🛒': { icon: 'ShoppingCart', color: BRAND },        // Shop
  '🍷': { icon: 'Wine', color: BRAND },                // Wine
  '🍺': { icon: 'Beer', color: BRAND },                // Beer / Brewery
  '🥂': { icon: 'Wine', color: BRAND },                // Champagne / Toast / Bar

  // Tours & experiences
  '⛵': { icon: 'Sailboat', color: BRAND },            // Boat / Cruise
  '🚁': { icon: 'Plane', color: BRAND },               // Helicopter
  '🎈': { icon: 'CloudSun', color: BRAND },            // Hot air balloon
  '👨‍🍳': { icon: 'ChefHat', color: BRAND },             // Cooking class
  '🚶': { icon: 'Footprints', color: BRAND },          // Walking tour

  // Transport
  '🛎️': { icon: 'BellRing', color: BRAND },            // Hotel check-in
  '🧳': { icon: 'Luggage', color: BRAND },             // Checkout
  '✈️': { icon: 'Plane', color: BRAND },               // Flight
  '🚂': { icon: 'TrainFront', color: BRAND },          // Train
  '🚗': { icon: 'Car', color: BRAND },                 // Car
  '🛬': { icon: 'PlaneLanding', color: BRAND },        // Arrival
  '🚆': { icon: 'TrainFront', color: BRAND },          // Train
  '⛴️': { icon: 'Ship', color: BRAND },                // Ferry
  '🚌': { icon: 'Bus', color: BRAND },                 // Bus
  '🚇': { icon: 'TrainFrontTunnel', color: BRAND },    // Metro
  '🚕': { icon: 'Car', color: BRAND },                 // Taxi
  '🏨': { icon: 'Hotel', color: BRAND },               // Hotel

  // Default
  '✨': { icon: 'Sparkles', color: BRAND },            // Default activity

  // ─── Meal emojis ─────────────────────────────────
  '🍝': { icon: 'UtensilsCrossed', color: BRAND },     // Italian
  '🍣': { icon: 'Fish', color: BRAND },                // Japanese / Sushi
  '🥟': { icon: 'Soup', color: BRAND },                // Chinese / Dumplings
  '🍜': { icon: 'Soup', color: BRAND },                // Thai / Vietnamese / Noodles
  '🍛': { icon: 'Flame', color: BRAND },               // Indian / Curry
  '🌮': { icon: 'Sandwich', color: BRAND },            // Mexican
  '🥐': { icon: 'Croissant', color: BRAND },           // French / Bakery
  '🥘': { icon: 'Soup', color: BRAND },                // Spanish / Stew
  '🫒': { icon: 'Leaf', color: BRAND },                // Greek / Mediterranean
  '🥩': { icon: 'Beef', color: BRAND },                // Korean BBQ / Steak
  '🧆': { icon: 'Circle', color: BRAND },              // Middle Eastern / Falafel
  '🍔': { icon: 'Sandwich', color: BRAND },            // American / Burger
  '🦞': { icon: 'Fish', color: BRAND },                // Seafood
  '🥗': { icon: 'Salad', color: BRAND },               // Vegetarian
  '🍦': { icon: 'IceCream', color: BRAND },            // Ice cream
  '🍰': { icon: 'Cake', color: BRAND },                // Dessert
  '🏔️': { icon: 'Mountain', color: BRAND },            // Alpine cuisine
  '🫕': { icon: 'Soup', color: BRAND },                // Swiss / Fondue
  '🍳': { icon: 'Egg', color: BRAND },                 // Breakfast
  '☕': { icon: 'Coffee', color: BRAND },               // Coffee / Cafe
  '🍸': { icon: 'Martini', color: BRAND },             // Cocktail bar
  '🍽️': { icon: 'Utensils', color: BRAND },            // Lunch (default)
  '🍴': { icon: 'Utensils', color: BRAND },            // Default meal

  // ─── Additional meal/food emojis (from real itineraries) ───
  '🍤': { icon: 'Fish', color: BRAND },                // Shrimp / Tempura
  '🦐': { icon: 'Fish', color: BRAND },                // Shrimp / Prawn
  '🥞': { icon: 'UtensilsCrossed', color: BRAND },     // Pancakes / Brunch
  '🍗': { icon: 'Drumstick', color: BRAND },           // Chicken / Poultry
  '🍚': { icon: 'Soup', color: BRAND },                // Rice / Asian
  '🥢': { icon: 'UtensilsCrossed', color: BRAND },     // Chopsticks / Asian dining
  '🥯': { icon: 'Croissant', color: BRAND },           // Bagel / Bakery
  '🐟': { icon: 'Fish', color: BRAND },                // Fish market
  '🦑': { icon: 'Fish', color: BRAND },                // Squid / Seafood
  '🍱': { icon: 'UtensilsCrossed', color: BRAND },     // Bento / Japanese
  '🍶': { icon: 'Wine', color: BRAND },                // Sake
  '🥙': { icon: 'Sandwich', color: BRAND },            // Pita / Kebab
  '🧀': { icon: 'Sandwich', color: BRAND },            // Cheese / Fondue
  '🍖': { icon: 'Beef', color: BRAND },                // Meat on bone / BBQ
  '🥪': { icon: 'Sandwich', color: BRAND },            // Sandwich
  '🧇': { icon: 'UtensilsCrossed', color: BRAND },     // Waffle / Brunch
  '🥖': { icon: 'Croissant', color: BRAND },           // Baguette / Bread
  '🍲': { icon: 'Soup', color: BRAND },                // Hot pot / Stew
  '🍞': { icon: 'Croissant', color: BRAND },           // Bread / Bakery
  '🍕': { icon: 'Pizza', color: BRAND },               // Pizza
  '🥡': { icon: 'UtensilsCrossed', color: BRAND },     // Takeout / Street food
  '🥑': { icon: 'Salad', color: BRAND },               // Avocado / Healthy
  '🌶️': { icon: 'Flame', color: BRAND },               // Spicy food
  '🌽': { icon: 'Salad', color: BRAND },               // Corn / Local produce
  '🍢': { icon: 'UtensilsCrossed', color: BRAND },     // Oden / Skewers
  '🌯': { icon: 'Sandwich', color: BRAND },            // Burrito / Wrap
  '🌭': { icon: 'Sandwich', color: BRAND },            // Hot dog / Street food
  '🥛': { icon: 'Coffee', color: BRAND },              // Milk / Dairy
  '🦪': { icon: 'Fish', color: BRAND },                // Oysters / Shellfish
  '🦀': { icon: 'Fish', color: BRAND },                // Crab / Seafood
  '🍫': { icon: 'Coffee', color: BRAND },              // Chocolate / Confection
  '🍟': { icon: 'Sandwich', color: BRAND },            // Fries / Fast food
  '🐙': { icon: 'Fish', color: BRAND },                // Octopus / Takoyaki
  '🍮': { icon: 'IceCream', color: BRAND },            // Flan / Pudding
  '🧋': { icon: 'CupSoda', color: BRAND },             // Bubble tea / Drinks
  '🥫': { icon: 'Soup', color: BRAND },                // Canned / Local speciality
  '🍹': { icon: 'Martini', color: BRAND },             // Tropical cocktail
  '🥣': { icon: 'Soup', color: BRAND },                // Cereal / Porridge
  '🥧': { icon: 'Croissant', color: BRAND },           // Pie / Pastry
  '🍡': { icon: 'UtensilsCrossed', color: BRAND },     // Dango / Japanese sweets
  '🥬': { icon: 'Salad', color: BRAND },               // Greens / Vegetarian
  '🍅': { icon: 'Salad', color: BRAND },               // Tomato / Mediterranean
  '🍌': { icon: 'Salad', color: BRAND },               // Banana / Tropical fruit
  '🥃': { icon: 'Wine', color: BRAND },                // Whiskey / Spirits
  '🐌': { icon: 'UtensilsCrossed', color: BRAND },     // Escargot / French
  '🥚': { icon: 'Egg', color: BRAND },                 // Egg dish
  '🐷': { icon: 'UtensilsCrossed', color: BRAND },     // Pork / Tonkatsu
  '🥜': { icon: 'UtensilsCrossed', color: BRAND },     // Nuts / Snacks
  '🥨': { icon: 'Croissant', color: BRAND },           // Pretzel / Bakery
  '🍇': { icon: 'Wine', color: BRAND },                // Grapes / Vineyard
  '🥥': { icon: 'CupSoda', color: BRAND },             // Coconut / Tropical

  // ─── Additional activity/venue emojis ────────────
  '🏮': { icon: 'Landmark', color: BRAND },            // Lantern / Night market
  '🎶': { icon: 'Music', color: BRAND },               // Music / Concert
  '🎵': { icon: 'Music', color: BRAND },               // Music / Performance
  '🦆': { icon: 'PawPrint', color: BRAND },            // Duck / Wildlife
  '🦢': { icon: 'PawPrint', color: BRAND },            // Swan / Nature
  '🕯️': { icon: 'Flame', color: BRAND },               // Candle / Atmosphere
  '🐓': { icon: 'PawPrint', color: BRAND },            // Rooster / Farm
  '🦖': { icon: 'PawPrint', color: BRAND },            // Dinosaur / Museum
  '🏫': { icon: 'Building2', color: BRAND },           // School / Cultural center
  '🌆': { icon: 'Building2', color: BRAND },           // Cityscape / Skyline
  '🌸': { icon: 'Flower2', color: BRAND },             // Cherry blossom / Garden
  '🐸': { icon: 'PawPrint', color: BRAND },            // Frog / Nature
  '🤖': { icon: 'Cpu', color: BRAND },                 // Robot / Tech attraction
  '🔥': { icon: 'Flame', color: BRAND },               // Hot / Trending

  // ─── Additional animals & wildlife ────────────
  '🐍': { icon: 'Eye', color: BRAND },                  // Snake / Reptile viewing
  '🐘': { icon: 'PawPrint', color: BRAND },            // Elephant / Safari
  '🐒': { icon: 'PawPrint', color: BRAND },            // Monkey / Wildlife
  '🦅': { icon: 'PawPrint', color: BRAND },            // Eagle / Birds
  '🐕': { icon: 'PawPrint', color: BRAND },            // Dog / Pet-friendly
  '🐢': { icon: 'PawPrint', color: BRAND },            // Turtle / Sea life
  '🦜': { icon: 'PawPrint', color: BRAND },            // Parrot / Aviary
  '🐬': { icon: 'Fish', color: BRAND },                // Dolphin / Marine
  '🐋': { icon: 'Fish', color: BRAND },                // Whale watching
  '🦈': { icon: 'Fish', color: BRAND },                // Shark / Aquarium
  '🦋': { icon: 'Flower2', color: BRAND },             // Butterfly garden
  '🐊': { icon: 'PawPrint', color: BRAND },            // Crocodile / Reptile
  '🦩': { icon: 'PawPrint', color: BRAND },            // Flamingo / Wildlife
  '🐫': { icon: 'PawPrint', color: BRAND },            // Camel / Desert
  '🦒': { icon: 'PawPrint', color: BRAND },            // Giraffe / Zoo

  // ─── Additional venues & activities ────────────
  '🎪': { icon: 'Drama', color: BRAND },               // Circus / Show
  '🎠': { icon: 'Sparkles', color: BRAND },            // Carousel / Amusement
  '🏟️': { icon: 'Building2', color: BRAND },           // Stadium / Sports
  '⚽': { icon: 'Target', color: BRAND },               // Football / Sports
  '🏀': { icon: 'Target', color: BRAND },              // Basketball / Sports
  '🎾': { icon: 'Target', color: BRAND },              // Tennis / Sports
  '🏋️': { icon: 'Zap', color: BRAND },                 // Gym / Fitness
  '🧖': { icon: 'Flower2', color: BRAND },             // Sauna / Hammam
  '🛁': { icon: 'Droplets', color: BRAND },            // Hot spring / Onsen
  '⛲': { icon: 'Droplets', color: BRAND },             // Fountain / Square
  '🌋': { icon: 'Mountain', color: BRAND },            // Volcano
  '🏝️': { icon: 'Umbrella', color: BRAND },            // Island / Tropical
  '🏗️': { icon: 'Building2', color: BRAND },           // Architecture
  '🎡': { icon: 'Sparkles', color: BRAND },            // Ferris wheel
  '💎': { icon: 'Sparkles', color: BRAND },            // Jewels / Luxury
  '📚': { icon: 'Building2', color: BRAND },           // Library / Books
  '🔭': { icon: 'Star', color: BRAND },                // Observatory / Stargazing
  '🎤': { icon: 'Music', color: BRAND },               // Karaoke / Concert
  '💰': { icon: 'Landmark', color: BRAND },            // Money / Budget
  '🪷': { icon: 'Flower2', color: BRAND },             // Lotus / Temple
  '🧲': { icon: 'Sparkles', color: BRAND },            // Magnet / Attraction
};

/**
 * Look up the Lucide icon name for a given emoji string.
 * @param {string} emoji - The emoji character(s)
 * @returns {{ icon: string, color: string } | null} - Lucide icon name + suggested color, or null
 */
export function getIconForEmoji(emoji) {
  if (!emoji) return null;
  // Normalize: trim whitespace, remove variation selectors
  const normalized = emoji.trim().replace(/\uFE0F/g, '');

  // Try direct match first
  if (EMOJI_TO_ICON[emoji]) return EMOJI_TO_ICON[emoji];

  // Try normalized match
  for (const [key, value] of Object.entries(EMOJI_TO_ICON)) {
    if (key.replace(/\uFE0F/g, '') === normalized) return value;
  }

  return null;
}

/**
 * Get all available Lucide icon names used in the mapping.
 * Useful for tree-shaking imports.
 */
export function getUsedIconNames() {
  return [...new Set(Object.values(EMOJI_TO_ICON).map(v => v.icon))];
}

export default EMOJI_TO_ICON;
